import { Text, View, ScrollView, Pressable, RefreshControl, useWindowDimensions, Linking, Platform } from 'react-native';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import Avatar from '@/components/People/Avatar';
import {
    deleteTrainingModule, saveTrainingClass, deleteTrainingClass,
    saveTrainingRequirement,
    enrollPeople, getRoleMembersForEnrollment,
    dropEnrollment, completeEnrollment, graduateEnrollment,
    markClassCompleted, unmarkClassCompleted,
    markRequirementCompleted, unmarkRequirementCompleted,
    deleteClassInstance,
    deleteTrainingTerm,
    uploadTrainingFile, downloadTrainingFile, deleteTrainingFile, deleteTrainingEnrollment,
} from '@/data/training';
import { ActivityIndicator } from 'react-native-paper';
import * as DocumentPicker from 'expo-document-picker';
import usePlatform from '@/utils/hooks/usePlatform';
import { showAlert } from '@/utils/showAlert';
import { useNavigation, useRouter, useLocalSearchParams } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import { TabView } from 'react-native-tab-view';
import ReachTabBar from '@/components/General/ReachTabBar';
import Unicon from '@/components/General/Unicon';
import ContentLoader from '@/components/General/ContentLoader';
import ActionButton from '@/components/General/ActionButton';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import SheetModal from '@/components/General/SheetModal';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import SearchBox from '@/components/General/inputs/SearchBox';
import Input from '@/components/General/inputs/Input';
import SelectInput from '@/components/General/inputs/SelectInput';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import Toast from 'react-native-toast-message';
import React from 'react';
import PageTitle from '@/components/General/PageTitle';
import { saveToRecents } from '@/utils/recentlyVisited';
import useSaveShortcut from '@/utils/hooks/useSaveShortcut';

const getStatusColors = (status) => {
    switch (status) {
        case "ACTIVE": return { bg: "successContainer", text: "success" };
        case "ARCHIVED": return { bg: "surfaceVariant", text: "textSecondary" };
        case "DRAFT": return { bg: "warningContainer", text: "warning" };
        default: return { bg: "surface", text: "onSurface" };
    }
};

const getEnrollmentStatusColors = (status) => {
    switch (status) {
        case "ENROLLED": return { bg: "primaryContainer", text: "primary" };
        case "COMPLETED": return { bg: "successContainer", text: "success" };
        case "GRADUATED": return { bg: "successContainer", text: "success" };
        case "DROPPED": return { bg: "errorContainer", text: "error" };
        case "UNENROLLED": return { bg: "surfaceVariant", text: "textSecondary" };
        default: return { bg: "surface", text: "onSurface" };
    }
};

const getTeacherAssignmentDisplay = (status) => {
    switch (status) {
        case "ACCEPTED": return { label: "Accepted", bg: "successContainer", text: "success" };
        case "DECLINED": return { label: "Declined", bg: "errorContainer", text: "error" };
        case "NO_RESPONSE": return { label: "Pending", bg: "warningContainer", text: "warning" };
        default: return null;
    }
};

// ============ MODULE DETAIL SCREEN ============
export default function TrainingModuleDetail() {
    const { moduleID } = useLocalSearchParams();
    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const layout = useWindowDimensions();
    const { isNative } = usePlatform();
    const isWideScreen = layout.width >= 900;

    const { data: loggedInUser } = Query.people.loggedInUser();
    const saveShortcut = useSaveShortcut(loggedInUser?.id);
    const { data: trainingModule, isLoading, isFetching } = Query.trainingModules.byId({ id: moduleID });

    // Track recently visited
    useEffect(() => {
        if (trainingModule?.id) {
            saveToRecents({
                id: trainingModule.id,
                url: `/training/${trainingModule.id}`,
                title: trainingModule.name,
                params: { moduleID: trainingModule.id },
            });
        }
    }, [trainingModule?.id]);

    const [tabIndex, setTabIndex] = useState(0);
    const [addClassSheetOpen, setAddClassSheetOpen] = useState(false);
    const [addRequirementSheetOpen, setAddRequirementSheetOpen] = useState(false);
    const [selectedPeople, setSelectedPeople] = useState([]);
    const [newClassName, setNewClassName] = useState('');
    const [newClassDescription, setNewClassDescription] = useState('');
    const [newReqName, setNewReqName] = useState('');
    const [newReqDescription, setNewReqDescription] = useState('');
    const [newReqClassID, setNewReqClassID] = useState(null);
    const [newReqDueDate, setNewReqDueDate] = useState(null);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [scheduleTabIndex, setScheduleTabIndex] = useState(0);
    const [selectedTermFilter, setSelectedTermFilter] = useState('all');
    const [enrollmentTabIndex, setEnrollmentTabIndex] = useState(0);
    const [pendingClassIDs, setPendingClassIDs] = useState(new Set());
    const [pendingReqIDs, setPendingReqIDs] = useState(new Set());
    const [classSearch, setClassSearch] = useState('');
    const [enrollmentSearch, setEnrollmentSearch] = useState('');
    const [enrollPeopleOpen, setEnrollPeopleOpen] = useState(false);
    const [enrollRoleOpen, setEnrollRoleOpen] = useState(false);

    const activityRoutes = useMemo(() => [
        { key: 'classes', title: 'Classes' },
        { key: 'enrollments', title: 'Enrollment' },
        { key: 'instances', title: 'Schedule' },
    ], [trainingModule]);

    const mobileRoutes = useMemo(() => [
        { key: 'info', title: 'Info' },
        ...activityRoutes,
    ], [activityRoutes]);

    useEffect(() => {
        if (trainingModule) {
            const options = [];
            if (trainingModule?.id) {
                options.push({
                    name: "Save to Shortcuts",
                    icon: dropdownIcons.favorite,
                    onSelect: () => saveShortcut({ screen: `/training/${trainingModule.id}`, label: trainingModule?.name || 'Training Module', icon: 'school-outline' })
                });
            }
            if (loggedInUser?.can?.("Edit Training")) {
                options.push({ name: "Edit Module", onSelect: () => router.push({ pathname: "/training/create", params: { id: moduleID } }) });
            }
            if (loggedInUser?.can?.("Delete Training")) {
                options.push({
                    name: "Delete Module", destructive: true,
                    onSelect: () => showAlert({
                        title: "Delete Training Module?",
                        message: "This will delete the module and all its classes, requirements, and enrollments.",
                        confirmText: "Delete", cancelText: "Cancel",
                        onConfirm: () => deleteModuleMutation.mutate(moduleID)
                    })
                });
            }

            navigation.setOptions({
                headerTitle: () => <PageTitle title={trainingModule?.name} subtitle="Training Module" />,
                headerRight: options.length > 0 ? () => <DropdownMenu testID="HeaderMenu" options={options} /> : undefined
            });
        }
    }, [trainingModule, loggedInUser, saveShortcut]);

    // ── Helpers ──
    const checkAutoComplete = (enrollmentID, { markedClassID, markedReqID } = {}) => {
        const enrollment = trainingModule?.Enrollments?.find(e => e.id === enrollmentID);
        if (!enrollment || enrollment.status !== 'ENROLLED') return;

        const classes = trainingModule?.Classes || [];
        const requirements = trainingModule?.Requirements || [];
        const completedClassIDs = new Set(enrollment.ClassCompletions?.map(c => c.classID) || []);
        const completedReqIDs = new Set(enrollment.RequirementCompletions?.map(r => r.requirementID) || []);

        if (markedClassID) completedClassIDs.add(markedClassID);
        if (markedReqID) completedReqIDs.add(markedReqID);

        const totalItems = classes.length + requirements.length;
        const completedItems = completedClassIDs.size + completedReqIDs.size;

        if (totalItems > 0 && completedItems >= totalItems) {
            completeMutation.mutate({ enrollmentID });
        }
    };

    // ── Mutations ──
    const invalidateModule = () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.people.loggedInUser() });
        queryClient.invalidateQueries({ queryKey: ['trainingModules'] });
    };

    const deleteModuleMutation = useMutation({
        mutationFn: deleteTrainingModule,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['trainingModules'] });
            router.dismissTo('/training');
        }
    });

    const enrollMutation = useMutation({
        mutationFn: enrollPeople,
        onSuccess: () => { invalidateModule(); setSelectedPeople([]); }
    });

    const dropMutation = useMutation({ mutationFn: dropEnrollment, onSuccess: invalidateModule });
    const completeMutation = useMutation({
        mutationFn: completeEnrollment,
        onSuccess: (data) => {
            invalidateModule();
            if (data?.dependentModules?.length > 0 && data?.personID) {
                const nextModule = data.dependentModules[0];
                showAlert({
                    title: `Enroll in "${nextModule.name}"?`,
                    subtitle: `This student has completed the prerequisite. Would you like to enroll them in ${nextModule.name}?`,
                    confirmText: "Enroll",
                    cancelText: "Not Now",
                    onConfirm: () => {
                        enrollMutation.mutate({
                            moduleID: nextModule.id,
                            personIDs: [data.personID],
                            enrolledByID: loggedInUser?.id,
                            skipPrerequisiteCheck: true,
                        });
                    },
                });
            }
        },
    });
    const graduateMutation = useMutation({ mutationFn: graduateEnrollment, onSuccess: invalidateModule });
    const deleteMutation = useMutation({ mutationFn: deleteTrainingEnrollment, onSuccess: invalidateModule });

    const addClassMutation = useMutation({
        mutationFn: saveTrainingClass,
        onSuccess: () => { invalidateModule(); setAddClassSheetOpen(false); setNewClassName(''); setNewClassDescription(''); }
    });

    const deleteClassMutation = useMutation({ mutationFn: deleteTrainingClass, onSuccess: invalidateModule });

    const addReqMutation = useMutation({
        mutationFn: saveTrainingRequirement,
        onSuccess: () => { invalidateModule(); setAddRequirementSheetOpen(false); setNewReqName(''); setNewReqDescription(''); setNewReqClassID(null); setNewReqDueDate(null); }
    });

    const markClassMutation = useMutation({
        mutationFn: markClassCompleted,
        onMutate: (vars) => setPendingClassIDs(prev => new Set(prev).add(vars.classID)),
        onSettled: (_, __, vars) => setPendingClassIDs(prev => { const s = new Set(prev); s.delete(vars.classID); return s; }),
        onSuccess: (_, vars) => {
            invalidateModule();
            checkAutoComplete(vars.enrollmentID, { markedClassID: vars.classID });
        },
    });
    const unmarkClassMutation = useMutation({
        mutationFn: unmarkClassCompleted,
        onMutate: (vars) => setPendingClassIDs(prev => new Set(prev).add(vars.classID)),
        onSettled: (_, __, vars) => setPendingClassIDs(prev => { const s = new Set(prev); s.delete(vars.classID); return s; }),
        onSuccess: invalidateModule,
    });
    const markReqMutation = useMutation({
        mutationFn: markRequirementCompleted,
        onMutate: (vars) => setPendingReqIDs(prev => new Set(prev).add(vars.requirementID)),
        onSettled: (_, __, vars) => setPendingReqIDs(prev => { const s = new Set(prev); s.delete(vars.requirementID); return s; }),
        onSuccess: (_, vars) => {
            invalidateModule();
            checkAutoComplete(vars.enrollmentID, { markedReqID: vars.requirementID });
        },
    });
    const unmarkReqMutation = useMutation({
        mutationFn: unmarkRequirementCompleted,
        onMutate: (vars) => setPendingReqIDs(prev => new Set(prev).add(vars.requirementID)),
        onSettled: (_, __, vars) => setPendingReqIDs(prev => { const s = new Set(prev); s.delete(vars.requirementID); return s; }),
        onSuccess: invalidateModule,
    });

    const deleteInstanceMutation = useMutation({ mutationFn: deleteClassInstance, onSuccess: invalidateModule });
    const deleteTermMutation = useMutation({
        mutationFn: deleteTrainingTerm,
        onSuccess: () => { invalidateModule(); setSelectedTermFilter('all'); },
    });

    const refresh = useCallback(() => invalidateModule(), [moduleID]);

    // Section visibility state (no longer used with TabView, kept for potential future use)
    // eslint-disable-next-line no-unused-vars
    const [_showInactiveStudents, _setShowInactiveStudents] = useState({
        COMPLETED: false, DROPPED: false, UNENROLLED: false,
    });

    const sortFn = useCallback((a, b) => a.Person?.firstName?.localeCompare(b.Person?.lastName) || 0, []);

    const activeEnrollments = trainingModule?.Enrollments?.filter(e => e.status === 'ENROLLED').sort(sortFn) || [];
    const completedEnrollments = trainingModule?.Enrollments?.filter(e => e.status === 'COMPLETED').sort(sortFn) || [];
    const graduatedEnrollments = trainingModule?.Enrollments?.filter(e => e.status === 'GRADUATED').sort(sortFn) || [];
    const droppedEnrollments = trainingModule?.Enrollments?.filter(e => e.status === 'DROPPED').sort(sortFn) || [];
    const allClasses = trainingModule?.Classes || [];
    const allRequirements = trainingModule?.Requirements || [];
    const statusColors = getStatusColors(trainingModule?.status);

    const now = new Date();
    const classIndexMap = useMemo(() => {
        const map = {};
        (trainingModule?.Classes || []).forEach((cls, i) => { map[cls.id] = i + 1; });
        return map;
    }, [trainingModule]);
    const terms = trainingModule?.Terms || [];
    const termNameMap = useMemo(() => {
        const map = {};
        for (const t of terms) map[t.id] = t.name;
        return map;
    }, [terms]);

    const termFilterOptions = useMemo(() => [
        { label: 'All Terms', value: 'all' },
        ...terms.map(t => ({ label: t.name, value: t.id })),
        { label: 'No Term', value: 'none' },
    ], [terms]);

    const allInstances = useMemo(() => {
        const instances = [];
        for (const cls of (trainingModule?.Classes || [])) {
            for (const inst of (cls.Instances || [])) {
                const teacher = inst.Teacher || null;
                let teacherAssignmentStatus = null;
                if (teacher && inst.taskInstanceID && inst.TaskInstance?.Assignees) {
                    const assignee = inst.TaskInstance.Assignees.find(a => a.personID === teacher.id);
                    teacherAssignmentStatus = assignee?.assignmentResponse || null;
                }
                instances.push({
                    ...inst,
                    className: cls.name,
                    classID: cls.id,
                    termName: inst.termID ? termNameMap[inst.termID] : null,
                    teacher,
                    teacherAssignmentStatus,
                });
            }
        }
        return instances.sort((a, b) => new Date(b.scheduledAt) - new Date(a.scheduledAt));
    }, [trainingModule, termNameMap]);

    const filteredInstances = useMemo(() => {
        if (selectedTermFilter === 'all') return allInstances;
        if (selectedTermFilter === 'none') return allInstances.filter(i => !i.termID);
        return allInstances.filter(i => i.termID === selectedTermFilter);
    }, [allInstances, selectedTermFilter]);

    const upcomingInstances = filteredInstances.filter(i => new Date(i.scheduledAt) >= now);
    const passedInstances = filteredInstances.filter(i => new Date(i.scheduledAt) < now);

    // ============ MODULE INFO PANEL ============
    const ModuleInfoPanel = useCallback(() => (
        <View style={styles.leftPanel}>
            <ScrollView
                contentContainerStyle={styles.leftPanelContent}
                keyboardShouldPersistTaps="handled"
                refreshControl={<RefreshControl refreshing={isFetching} onRefresh={refresh}/>}
                showsVerticalScrollIndicator={false}
            >
                {/* Header Card */}
                <View style={styles.headerCard}>
                    <View style={styles.infoHeader}>
                        <View style={styles.moduleIcon}>
                            <Unicon name="school-outline" size={28} colorVariant="training" />
                        </View>
                        <View style={styles.infoHeaderText}>
                            <Text style={styles.moduleName}>{trainingModule?.name}</Text>
                            <View style={styles.statusBadge(statusColors.bg, statusColors.text)}>
                                <Text style={styles.statusBadgeText(statusColors.text)}>{trainingModule?.status}</Text>
                            </View>
                        </View>
                    </View>

                    {trainingModule?.description && <Text style={styles.moduleDescription}>{trainingModule?.description}</Text>}

                    {trainingModule?.prerequisiteModuleID && (
                        <View style={styles.prerequisiteRow}>
                            <Unicon name="git-branch-outline" size={16} colorVariant="textSecondary" />
                            <Text style={styles.prerequisiteLabel}>Prerequisite:</Text>
                            <Pressable onPress={() => router.push("/training/" + trainingModule?.prerequisiteModuleID)}>
                                <Text style={styles.prerequisiteLink}>{trainingModule?.PrerequisiteModule?.name || 'View Module'}</Text>
                            </Pressable>
                        </View>
                    )}

                    {(trainingModule?.Teachers?.length > 0 || trainingModule?.Teacher) && (
                        <View style={styles.teachersSection}>
                            <Text style={styles.teacherLabel}>{(trainingModule?.Teachers?.length || 1) > 1 ? 'Teachers' : 'Teacher'}</Text>
                            <View style={styles.teachersList}>
                                {(trainingModule?.Teachers?.length > 0 ? trainingModule.Teachers : trainingModule?.Teacher ? [trainingModule?.Teacher] : []).map(teacher => (
                                    <Pressable key={teacher.id} onPress={() => router.push("/people/" + teacher.id)} style={styles.teacherChip}>
                                        <Avatar profile={teacher} size={24} disableBorder />
                                        <Text style={styles.teacherName}>{teacher.firstName} {teacher.lastName}</Text>
                                    </Pressable>
                                ))}
                            </View>
                        </View>
                    )}

                    {/* Stats Row */}
                    <View style={styles.statsRow}>
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Classes</Text>
                            <Text style={styles.statValue}>{allClasses.length}</Text>
                        </View>
                        <View style={styles.statDivider} />
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Enrolled</Text>
                            <Text style={styles.statValue}>{activeEnrollments.length}</Text>
                        </View>
                        <View style={styles.statDivider} />
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Requirements</Text>
                            <Text style={styles.statValue}>{allRequirements.length}</Text>
                        </View>
                    </View>

                    {/* Action Buttons */}
                    <View style={styles.actionRow}>
                        {loggedInUser?.can?.("Edit Training") && (
                            <ActionButton iconName="create" text="Edit Module" onPress={() => router.push({ pathname: "/training/create", params: { id: moduleID } })} />
                        )}
                        {loggedInUser?.can?.("Create Training") && (
                            <ActionButton iconName="add-circle-outline" text="Schedule Class" onPress={() => router.push({ pathname: `/training/${moduleID}/instance/create`, params: { moduleID } })} />
                        )}
                    </View>
                </View>
            </ScrollView>
        </View>
    ), [trainingModule, isFetching, loggedInUser, activeEnrollments, allRequirements, statusColors]);

    // ── CLASSES TAB ──
    const renderClassesTab = () => {
        const filteredClasses = classSearch
            ? allClasses.filter(c => c.name?.toLowerCase().includes(classSearch.toLowerCase()))
            : allClasses;
        return (
        <View style={styles.scheduleTabContainer}>
            <View style={styles.tabToolbar}>
                <View style={styles.tabToolbarSearch}>
                    <SearchBox searchText={classSearch} onChangeText={setClassSearch} placeholder="Search classes..." textChangeDelay={0} />
                </View>
                {loggedInUser?.can?.("Create Training") && (
                    <Pressable testID="add-class-btn" onPress={() => setAddClassSheetOpen(true)} style={styles.tabToolbarIconBtn}>
                        <Unicon name="add-circle-outline" size={24} colorVariant="primary" />
                    </Pressable>
                )}
            </View>
            <ScrollView style={styles.tabContent} keyboardShouldPersistTaps="handled" refreshControl={<RefreshControl refreshing={isFetching} onRefresh={refresh} />}>
                {filteredClasses.length === 0
                    ? <EmptyListPlaceholder icon="book-outline" message={classSearch ? "No matching classes" : "No classes added yet"} />
                    : filteredClasses.map((cls, i) => (
                        <View key={cls.id} style={styles.classCard}>
                            <View style={styles.classCardHeader}>
                                <View style={styles.classCardLeft}>
                                    <View style={styles.classNumber}>
                                        <Text style={styles.classNumberText}>{i + 1}</Text>
                                    </View>
                                    <View>
                                        <Text style={styles.className}>{cls.name}</Text>
                                        {!!cls.description && <Text style={styles.classDescription}>{cls.description}</Text>}
                                    </View>
                                </View>
                                {loggedInUser?.can?.("Delete Training") && (
                                    <Pressable testID={`delete-class-btn-${cls.id}`} onPress={() => showAlert({
                                        title: `Delete "${cls.name}"?`, confirmText: "Delete", cancelText: "Cancel",
                                        onConfirm: () => deleteClassMutation.mutate(cls.id)
                                    })}>
                                        <Unicon name="trash-outline" size={18} colorVariant="error" />
                                    </Pressable>
                                )}
                            </View>
                            {cls.Requirements?.length > 0 && (
                                <View style={styles.classReqList}>
                                    {cls.Requirements.map(req => (
                                        <View key={req.id} style={styles.reqChip}>
                                            <Unicon name="checkmark-circle-outline" size={16} colorVariant="textSecondary" />
                                            <View style={{flex: 1}}>
                                                <Text style={styles.reqChipText}>{req.name}</Text>
                                                {req.description ? <Text style={styles.reqChipType}>{req.description}</Text> : null}
                                            </View>
                                        </View>
                                    ))}
                                </View>
                            )}
                            <View style={styles.classFilesSection}>
                                <View style={styles.classFilesHeader}>
                                    <View style={styles.classFilesLabel}>
                                        <Unicon name="attach-outline" size={14} colorVariant="textSecondary" />
                                        <Text style={styles.classFilesLabelText}>{cls.Files?.length || 0} file{cls.Files?.length !== 1 ? 's' : ''}</Text>
                                    </View>
                                    {loggedInUser?.can?.("Create Training") && (
                                        <Pressable
                                            onPress={async () => {
                                                const result = await DocumentPicker.getDocumentAsync({ multiple: false });
                                                if (result.canceled || !result.assets?.length) return;
                                                const picked = result.assets[0];
                                                setUploadingFile(true);
                                                try {
                                                    await uploadTrainingFile({ moduleID, classID: cls.id, file: { name: picked.name, uri: picked.uri, size: picked.size, mimeType: picked.mimeType }, uploadedByID: loggedInUser?.id });
                                                    invalidateModule();
                                                } finally {
                                                    setUploadingFile(false);
                                                }
                                            }}
                                            style={styles.classFileUploadButton}
                                        >
                                            {uploadingFile
                                                ? <ActivityIndicator size={12} />
                                                : <Unicon name="cloud-upload-outline" size={14} colorVariant="primary" />
                                            }
                                        </Pressable>
                                    )}
                                </View>
                                {cls.Files?.map((f, fi) => (
                                    <Pressable
                                        key={f.id}
                                        style={[styles.classFileItem, fi < cls.Files.length - 1 && styles.classFileItemBorder]}
                                        onPress={async () => {
                                            const url = await downloadTrainingFile(f.storageKey);
                                            if (url) {
                                                if (Platform.OS === 'web') window.open(url, '_blank');
                                                else Linking.openURL(url);
                                            }
                                        }}
                                    >
                                        <Unicon name="document-outline" size={14} colorVariant="primary" />
                                        <Text style={styles.classFileName} numberOfLines={1}>{f.fileName}</Text>
                                        {loggedInUser?.can?.("Delete Training") && (
                                            <Pressable hitSlop={8} onPress={(e) => {
                                                e.stopPropagation?.();
                                                showAlert({
                                                    title: `Delete "${f.fileName}"?`, confirmText: "Delete", cancelText: "Cancel",
                                                    onConfirm: () => deleteTrainingFile({ fileID: f.id, storageKey: f.storageKey }).then(invalidateModule)
                                                });
                                            }}>
                                                <Unicon name="trash-outline" size={14} colorVariant="error" />
                                            </Pressable>
                                        )}
                                    </Pressable>
                                ))}
                            </View>
                        </View>
                    ))
                }
                <View style={{ height: 100 }} />
            </ScrollView>
        </View>
        );
    };

    // ── ENROLLMENTS TAB ──
    const enrollmentTabRoutes = useMemo(() => [
        { key: 'ENROLLED', title: `Active (${activeEnrollments.length})` },
        { key: 'COMPLETED', title: `Completed (${completedEnrollments.length})` },
        { key: 'GRADUATED', title: `Graduated (${graduatedEnrollments.length})` },
        { key: 'DROPPED', title: `Dropped (${droppedEnrollments.length})` },
    ].filter(r => {
        const count = parseInt(r.title.match(/\d+/)?.[0] || 0);
        return count > 0 || r.key === 'ENROLLED';
    }), [activeEnrollments.length, completedEnrollments.length, graduatedEnrollments.length, droppedEnrollments.length]);

    const renderEnrollmentList = (enrollments, showGraduateAll = false) => (
        <ScrollView style={styles.tabContent} keyboardShouldPersistTaps="handled" refreshControl={<RefreshControl refreshing={isFetching} onRefresh={refresh} />}>
            {loggedInUser?.can?.("Manage Training Enrollments") && showGraduateAll && enrollments.length > 0 && (
                <View style={styles.actionRow}>
                    <ActionButton 
                        iconName="school-outline" 
                        text={`Mark All as Graduated (${enrollments.length})`} 
                        onPress={() => showAlert({
                            title: `Graduate all ${enrollments.length} completed students?`,
                            confirmText: "Graduate All",
                            cancelText: "Cancel",
                            onConfirm: () => {
                                enrollments.forEach(e => graduateMutation.mutate({ enrollmentID: e.id }));
                            }
                        })}
                    />
                </View>
            )}
            {enrollments.length === 0
                ? <EmptyListPlaceholder icon="people-outline" message="No students in this category" />
                : enrollments.map(enrollment => (
                    <EnrollmentRow
                        key={enrollment.id}
                        enrollment={enrollment}
                        classes={allClasses}
                        requirements={allRequirements}
                        loggedInUser={loggedInUser}
                        onDelete={() => deleteMutation.mutate({ enrollmentID: enrollment.id })}
                        onDrop={() => dropMutation.mutate({ enrollmentID: enrollment.id })}
                        onComplete={() => completeMutation.mutate({ enrollmentID: enrollment.id })}
                        onGraduate={() => graduateMutation.mutate({ enrollmentID: enrollment.id })}
                        onMarkClass={(classID) => markClassMutation.mutate({ enrollmentID: enrollment.id, classID, markedByID: loggedInUser?.id })}
                        onUnmarkClass={(classID) => unmarkClassMutation.mutate({ enrollmentID: enrollment.id, classID })}
                        onMarkReq={(reqID) => markReqMutation.mutate({ enrollmentID: enrollment.id, requirementID: reqID, markedByID: loggedInUser?.id })}
                        onUnmarkReq={(reqID) => unmarkReqMutation.mutate({ enrollmentID: enrollment.id, requirementID: reqID })}
                        router={router}
                        pendingClassIDs={pendingClassIDs}
                        pendingReqIDs={pendingReqIDs}
                    />
                ))
            }
            <View style={{ height: 100 }} />
        </ScrollView>
    );

    const renderEnrollmentsTab = () => {
        const q = enrollmentSearch.toLowerCase();
        const filterEnrollments = (list) => q
            ? list.filter(e => {
                const p = e.Person;
                return `${p?.firstName} ${p?.lastName}`.toLowerCase().includes(q);
            })
            : list;

        const enrollMenuOptions = loggedInUser?.can?.("Manage Training Enrollments") ? [
            { name: "Enroll by User", icon: { web: "person-add-outline" }, onSelect: () => setEnrollPeopleOpen(true) },
            { name: "Enroll by Role", icon: { web: "people-outline" }, onSelect: () => setEnrollRoleOpen(true) },
        ] : [];

        return (
        <View style={styles.scheduleTabContainer}>
            <View style={styles.tabToolbar}>
                <View style={styles.tabToolbarSearch}>
                    <SearchBox searchText={enrollmentSearch} onChangeText={setEnrollmentSearch} placeholder="Search enrollments..." textChangeDelay={0} />
                </View>
                {loggedInUser?.can?.("Manage Training Enrollments") && (
                    <DropdownMenu
                        testID="enroll-menu"
                        options={enrollMenuOptions}
                        triggerComponent={
                            <View style={styles.tabToolbarIconBtn}>
                                <Unicon name="person-add-outline" size={24} colorVariant="primary" />
                            </View>
                        }
                    />
                )}
            </View>
            <TabView
                swipeEnabled={isNative}
                navigationState={{ index: enrollmentTabIndex, routes: enrollmentTabRoutes }}
                onIndexChange={setEnrollmentTabIndex}
                initialLayout={{ width: layout.width }}
                renderTabBar={(props) => <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}><ReachTabBar scrollEnabled {...props} /></View>}
                lazy
                renderScene={({ route }) => {
                    switch (route.key) {
                        case 'ENROLLED': return renderEnrollmentList(filterEnrollments(activeEnrollments));
                        case 'COMPLETED': return renderEnrollmentList(filterEnrollments(completedEnrollments), true);
                        case 'GRADUATED': return renderEnrollmentList(filterEnrollments(graduatedEnrollments));
                        case 'DROPPED': return renderEnrollmentList(filterEnrollments(droppedEnrollments));
                        default: return null;
                    }
                }}
            />
        </View>
        );
    };

    // ── SCHEDULE TAB ──
    const scheduleTabRoutes = useMemo(() => [
        { key: 'upcoming', title: `Upcoming (${upcomingInstances.length})` },
        { key: 'past', title: `Past (${passedInstances.length})` },
    ], [upcomingInstances.length, passedInstances.length]);

    const renderInstanceCard = (inst) => {
        const classNum = classIndexMap[inst.classID];
        return (
            <Pressable key={inst.id} testID={`instance-card-${inst.id}`} onPress={() => router.push(`/training/${moduleID}/instance/${inst.id}`)} style={styles.instanceCard}>
                <View style={styles.instanceHeader}>
                    {classNum != null && (
                        <View style={styles.instanceNumber}>
                            <Text style={styles.instanceNumberText}>{classNum}</Text>
                        </View>
                    )}
                    <View style={{ flex: 1 }}>
                        <View style={styles.instanceTitleRow}>
                            <Text style={styles.instanceClassName}>{inst.className || 'Unknown Class'}</Text>
                            {inst.termName && (
                                <View style={styles.termBadge}>
                                    <Text style={styles.termBadgeText}>{inst.termName}</Text>
                                </View>
                            )}
                        </View>
                        <Text style={styles.instanceDate}>
                            {new Date(inst.scheduledAt).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </Text>
                        {inst.notes && <Text style={styles.instanceNotes}>{inst.notes}</Text>}
                        {inst.teacher && (() => {
                            const assignment = getTeacherAssignmentDisplay(inst.teacherAssignmentStatus);
                            return (
                                <Pressable style={styles.teacherRow} onPress={(e) => { e.stopPropagation?.(); router.push(`/people/${inst.teacher.id}`); }}>
                                    <Avatar profile={inst.teacher} size={20} disableBorder />
                                    <Text style={styles.teacherNameText}>{inst.teacher.firstName} {inst.teacher.lastName}</Text>
                                    {assignment && (
                                        <View style={styles.assignmentBadge(assignment.bg)}>
                                            <Text style={styles.assignmentBadgeText(assignment.text)}>{assignment.label}</Text>
                                        </View>
                                    )}
                                </Pressable>
                            );
                        })()}
                    </View>
                    {loggedInUser?.can?.("Delete Training") && (
                        <Pressable testID={`delete-instance-btn-${inst.id}`} onPress={(e) => {
                            e.stopPropagation?.();
                            showAlert({
                                title: 'Delete this scheduled instance?', confirmText: "Delete", cancelText: "Cancel",
                                onConfirm: () => deleteInstanceMutation.mutate(inst.id)
                            });
                        }}>
                            <Unicon name="trash-outline" size={18} colorVariant="error" />
                        </Pressable>
                    )}
                </View>
            </Pressable>
        );
    };

    const renderScheduleInstanceList = (instances) => (
        <ScrollView style={styles.tabContent} keyboardShouldPersistTaps="handled" refreshControl={<RefreshControl refreshing={isFetching} onRefresh={refresh}/>}>
            {instances.length === 0
                ? <EmptyListPlaceholder icon="calendar-outline" message="No class instances" />
                : instances.map(renderInstanceCard)
            }
            <View style={{ height: 100 }} />
        </ScrollView>
    );

    const renderScheduleTab = () => (
        <View style={styles.scheduleTabContainer}>
            {loggedInUser?.can?.("Create Training") && (
                <View style={styles.scheduleActionRow}>
                    <ActionButton iconName="add-circle-outline" text="Schedule Class" onPress={() => router.push({ pathname: `/training/${moduleID}/instance/create`, params: { moduleID } })} />
                    <ActionButton iconName="calendar-outline" text="Schedule a New Term" testID="schedule-new-term-btn" onPress={() => router.push({ pathname: `/training/${moduleID}/term/create`, params: { moduleID } })} />
                </View>
            )}
            {terms.length > 0 && (
                <View style={styles.termFilterRow}>
                    <SelectInput
                        title="Filter by Term"
                        value={selectedTermFilter}
                        options={termFilterOptions}
                        onUpdate={(value) => setSelectedTermFilter(value || 'all')}
                    />
                </View>
            )}
            {selectedTermFilter !== 'all' && selectedTermFilter !== 'none' && loggedInUser?.can?.("Edit Training") && (
                <View style={styles.termManageRow}>
                    <Pressable testID="edit-term-btn" style={styles.termManageBtn} onPress={() => router.push({ pathname: `/training/${moduleID}/term/create`, params: { moduleID, termID: selectedTermFilter } })}>
                        <Unicon name="create-outline" size={16} colorVariant="primary" />
                        <Text style={styles.termManageBtnText}>Edit Term</Text>
                    </Pressable>
                    <Pressable testID="delete-term-btn" style={[styles.termManageBtn, styles.termManageBtnDanger]} onPress={() => {
                        const term = terms.find(t => t.id === selectedTermFilter);
                        showAlert({
                            title: `Delete term "${term?.name ?? ''}"?`,
                            message: "This will permanently delete all class instances in this term and their teacher tasks.",
                            confirmText: "Delete", cancelText: "Cancel",
                            isDestructive: true,
                            onConfirm: () => deleteTermMutation.mutate(selectedTermFilter),
                        });
                    }}>
                        <Unicon name="trash-outline" size={16} colorVariant="error" />
                        <Text style={[styles.termManageBtnText, styles.termManageBtnTextDanger]}>Delete Term</Text>
                    </Pressable>
                </View>
            )}
            <TabView
                swipeEnabled={isNative}
                navigationState={{ index: scheduleTabIndex, routes: scheduleTabRoutes }}
                onIndexChange={setScheduleTabIndex}
                initialLayout={{ width: layout.width }}
                renderTabBar={(props) => <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}><ReachTabBar {...props} /></View>}
                lazy
                renderScene={({ route }) => {
                    switch (route.key) {
                        case 'upcoming': return renderScheduleInstanceList(upcomingInstances);
                        case 'past': return renderScheduleInstanceList(passedInstances);
                        default: return null;
                    }
                }}
            />
        </View>
    );

    // ============ ACTIVITY PANEL ============
    const renderActivityTabBar = useCallback((props) => <ReachTabBar {...props} />, []);

    const renderActivityScene = useCallback(({ route }) => {
        switch (route.key) {
            case 'classes': return renderClassesTab();
            case 'enrollments': return renderEnrollmentsTab();
            case 'instances': return renderScheduleTab();
            default: return null;
        }
    }, [renderClassesTab, renderEnrollmentsTab, renderScheduleTab]);

    // ============ MOBILE LAYOUT ============
    const renderMobileTabBar = useCallback((props) => <ReachTabBar {...props} scrollEnabled />, []);

    const renderMobileScene = useCallback(({ route }) => {
        if (route.key === 'info') return <ModuleInfoPanel />;
        return renderActivityScene({ route });
    }, [ModuleInfoPanel, renderActivityScene]);

    // ============ MAIN RENDER ============
    if (isLoading) return <ContentLoader />;
    if (!trainingModule) return <EmptyListPlaceholder icon="school-outline" message="Module not found" />;

    if (isWideScreen) {
        return (
            <View style={styles.container}>
                <View style={styles.twoColumn}>
                    <ModuleInfoPanel />
                    <View style={styles.rightPanel}>
                        <TabView
                            navigationState={{ index: tabIndex, routes: activityRoutes }}
                            renderScene={renderActivityScene}
                            onIndexChange={setTabIndex}
                            renderTabBar={renderActivityTabBar}
                            swipeEnabled={isNative}
                            lazy
                        />
                    </View>
                </View>

                {/* Add Class Sheet */}
                <SheetModal
                    title="Add Class"
                    isOpen={addClassSheetOpen}
                    onCancel={() => { setAddClassSheetOpen(false); setNewClassName(''); setNewClassDescription(''); }}
                    confirmText="Add"
                    onConfirm={() => {
                        if (!newClassName.trim()) {
                            Toast.show({ type: 'error', text2: 'Enter a class name' });
                            return;
                        }
                        addClassMutation.mutate({ moduleID, name: newClassName, description: newClassDescription, sortOrder: allClasses.length });
                    }}
                >
                    <Input title="Class Name" placeholder="e.g. Lesson 1" value={newClassName} onUpdate={setNewClassName} />
                    <Input title="Description (optional)" multiline value={newClassDescription} onUpdate={setNewClassDescription} />
                </SheetModal>

                {/* Add Requirement Sheet */}
                <SheetModal
                    title="Add Requirement"
                    isOpen={addRequirementSheetOpen}
                    onCancel={() => { setAddRequirementSheetOpen(false); setNewReqName(''); setNewReqDescription(''); setNewReqClassID(null); }}
                    confirmText="Add"
                    onConfirm={() => {
                        if (!newReqName.trim()) {
                            Toast.show({ type: 'error', text2: 'Enter a requirement name' });
                            return;
                        }
                        if (!newReqClassID) {
                            Toast.show({ type: 'error', text2: 'Select a class for this requirement' });
                            return;
                        }
                        addReqMutation.mutate({ moduleID, classID: newReqClassID, name: newReqName, description: newReqDescription, dueDate: newReqDueDate, sortOrder: 0 });
                    }}
                >
                    <Input title="Requirement Name" placeholder="e.g. Scripture Memorization" value={newReqName} onUpdate={setNewReqName} />
                    <SelectInput
                        title="Class"
                        value={newReqClassID ? allClasses.find(c => c.id === newReqClassID)?.name : null}
                        options={allClasses.map(c => c.name)}
                        onUpdate={(v) => setNewReqClassID(allClasses.find(c => c.name === v)?.id || null)}
                    />
                    <DatePickerInput title="Due Date (optional)" mode="date" value={newReqDueDate} onUpdate={setNewReqDueDate} />
                    <Input title="Description (optional)" multiline value={newReqDescription} onUpdate={setNewReqDescription} />
                </SheetModal>

                {/* Enroll Sheets */}
                <EnrollSheet
                    type="people"
                    selectedPeople={selectedPeople}
                    open={enrollPeopleOpen}
                    onClose={() => setEnrollPeopleOpen(false)}
                    onEnroll={(people) => {
                        setEnrollPeopleOpen(false);
                        if (!people?.length) return;
                        enrollMutation.mutate({ moduleID, personIDs: people.map(p => p.id), enrolledByID: loggedInUser?.id });
                    }}
                />
                <EnrollSheet
                    type="roles"
                    selectedPeople={selectedPeople}
                    open={enrollRoleOpen}
                    onClose={() => setEnrollRoleOpen(false)}
                    onEnroll={(role) => {
                        setEnrollRoleOpen(false);
                        if (!role?.id) return;
                        getRoleMembersForEnrollment(role.id).then(members => {
                            if (members?.length > 0) {
                                enrollMutation.mutate({ moduleID, personIDs: members.map(p => p.id), enrolledByID: loggedInUser?.id });
                            } else {
                                Toast.show({ type: 'error', text2: 'No members in this role' });
                            }
                        });
                    }}
                />
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <TabView
                navigationState={{ index: tabIndex, routes: mobileRoutes }}
                renderScene={renderMobileScene}
                onIndexChange={setTabIndex}
                renderTabBar={renderMobileTabBar}
                swipeEnabled={isNative}
                lazy
            />

            {/* Add Class Sheet */}
            <SheetModal
                title="Add Class"
                isOpen={addClassSheetOpen}
                onCancel={() => { setAddClassSheetOpen(false); setNewClassName(''); setNewClassDescription(''); }}
                confirmText="Add"
                onConfirm={() => {
                    if (!newClassName.trim()) {
                        Toast.show({ type: 'error', text2: 'Enter a class name' });
                        return;
                    }
                    addClassMutation.mutate({ moduleID, name: newClassName, description: newClassDescription, sortOrder: allClasses.length });
                }}
            >
                <Input title="Class Name" placeholder="e.g. Lesson 1" value={newClassName} onUpdate={setNewClassName} />
                <Input title="Description (optional)" multiline value={newClassDescription} onUpdate={setNewClassDescription} />
            </SheetModal>

            {/* Add Requirement Sheet */}
            <SheetModal
                title="Add Requirement"
                isOpen={addRequirementSheetOpen}
                onCancel={() => { setAddRequirementSheetOpen(false); setNewReqName(''); setNewReqDescription(''); setNewReqClassID(null); }}
                confirmText="Add"
                onConfirm={() => {
                    if (!newReqName.trim()) {
                        Toast.show({ type: 'error', text2: 'Enter a requirement name' });
                        return;
                    }
                    if (!newReqClassID) {
                        Toast.show({ type: 'error', text2: 'Select a class for this requirement' });
                        return;
                    }
                    addReqMutation.mutate({ moduleID, classID: newReqClassID, name: newReqName, description: newReqDescription, dueDate: newReqDueDate, sortOrder: 0 });
                }}
            >
                <Input title="Requirement Name" placeholder="e.g. Scripture Memorization" value={newReqName} onUpdate={setNewReqName} />
                <SelectInput
                    title="Class"
                    value={newReqClassID ? allClasses.find(c => c.id === newReqClassID)?.name : null}
                    options={allClasses.map(c => c.name)}
                    onUpdate={(v) => setNewReqClassID(allClasses.find(c => c.name === v)?.id || null)}
                />
                <DatePickerInput title="Due Date (optional)" mode="date" value={newReqDueDate} onUpdate={setNewReqDueDate} />
                <Input title="Description (optional)" multiline value={newReqDescription} onUpdate={setNewReqDescription} />
            </SheetModal>

            {/* Enroll Sheets */}
            <EnrollSheet
                type="people"
                selectedPeople={selectedPeople}
                open={enrollPeopleOpen}
                onClose={() => setEnrollPeopleOpen(false)}
                onEnroll={(people) => {
                    setEnrollPeopleOpen(false);
                    if (!people?.length) return;
                    enrollMutation.mutate({ moduleID, personIDs: people.map(p => p.id), enrolledByID: loggedInUser?.id });
                }}
            />
            <EnrollSheet
                type="roles"
                selectedPeople={selectedPeople}
                open={enrollRoleOpen}
                onClose={() => setEnrollRoleOpen(false)}
                onEnroll={(role) => {
                    setEnrollRoleOpen(false);
                    if (!role?.id) return;
                    getRoleMembersForEnrollment(role.id).then(members => {
                        if (members?.length > 0) {
                            enrollMutation.mutate({ moduleID, personIDs: members.map(p => p.id), enrolledByID: loggedInUser?.id });
                        } else {
                            Toast.show({ type: 'error', text2: 'No members in this role' });
                        }
                    });
                }}
            />
        </View>
    );
}

// ============ ENROLL SHEET ============
const EnrollSheet = ({ type, selectedPeople, onEnroll, open, onClose }) => {
    if (type === 'people') {
        return (
            <SheetSelector
                multiple
                type="people"
                addScreenName="people/create"
                fromScreen="training/[moduleID]/index"
                fromStoreKey="trainingEnrollments"
                title="Enroll People"
                value={selectedPeople}
                onChange={onEnroll}
                onCancel={onClose}
                open={open}
                forceModal
            >
                <View style={{ width: 0, height: 0 }} />
            </SheetSelector>
        );
    }
    return (
        <SheetSelector
            type="roles"
            addScreenName="roles/create"
            fromScreen="training/[moduleID]/index"
            fromStoreKey="trainingEnrollments"
            title="Enroll by Role"
            onChange={onEnroll}
            onCancel={onClose}
            open={open}
            forceModal
        >
            <View style={{ width: 0, height: 0 }} />
        </SheetSelector>
    );
};

// ============ ENROLLMENT ROW ============
const EnrollmentRow = React.memo(({ enrollment, classes, requirements, loggedInUser, onDelete, onDrop, onComplete, onGraduate, onMarkClass, onUnmarkClass, onMarkReq, onUnmarkReq, router, pendingClassIDs, pendingReqIDs }) => {
    const [expanded, setExpanded] = useState(false);
    const person = enrollment.Person;
    const completedClassIDs = new Set(enrollment.ClassCompletions?.map(c => c.classID) || []);
    const completedReqIDs = new Set(enrollment.RequirementCompletions?.map(r => r.requirementID) || []);
    const totalItems = classes.length + requirements.length;
    const completedItems = completedClassIDs.size + completedReqIDs.size;
    const percent = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
    const statusColors = getEnrollmentStatusColors(enrollment.status);
    const isGraduated = enrollment.status === 'GRADUATED';

    const menuOptions = [];
    if (loggedInUser?.can?.("Manage Training Enrollments")) {
        if (enrollment.status === 'COMPLETED' && onGraduate) {
            menuOptions.push({ name: "Graduate", onSelect: onGraduate });
        }
        if (percent === 100 && enrollment.status === 'ENROLLED') menuOptions.push({ name: "Mark Completed", onSelect: onComplete });
        menuOptions.push({ name: "Mark as Dropped", onSelect: () => showAlert({ title: `Mark ${person?.firstName} as dropped?`, confirmText: "Drop", cancelText: "Cancel", onConfirm: onDrop }) });
        menuOptions.push({ name: "Delete", destructive: true, onSelect: () => showAlert({ title: `Delete ${person?.firstName}'s enrollment?`, message: "This will permanently remove the enrollment and all associated records.", confirmText: "Delete", cancelText: "Cancel", onConfirm: onDelete }) });
    }

    return (
        <View style={styles.enrollmentCard}>
            <Pressable onPress={() => setExpanded(!expanded)} style={styles.enrollmentHeader}>
                <View style={styles.enrollmentPerson}>
                    <Pressable onPress={() => router.push("/people/" + person?.id)}>
                        <Avatar profile={person} size={36} disableBorder />
                    </Pressable>
                    <View style={{ flex: 1 }}>
                        <Text style={styles.enrollmentName}>{person?.firstName} {person?.lastName}</Text>
                        <View style={styles.enrollmentMeta}>
                            <View style={styles.progressBadge(statusColors.bg)}>
                                <Text style={styles.progressBadgeText(statusColors.text)}>{enrollment.status}</Text>
                            </View>
                            <Text style={styles.enrollmentPercent}>{percent}%</Text>
                        </View>
                    </View>
                </View>
                <View style={styles.enrollmentActions}>
                    {menuOptions.length > 0 && <DropdownMenu testID={`enrollment-row-menu-${enrollment.id}`} options={menuOptions} />}
                    <Unicon name={expanded ? "chevron-up-outline" : "chevron-down-outline"} size={18} colorVariant="textSecondary" />
                </View>
            </Pressable>

            <View style={styles.miniProgressBarBg}>
                <View style={styles.miniProgressBarFill(percent)} />
            </View>

            {expanded && (
                <View style={styles.enrollmentDetails}>
                    {classes.length > 0 && (
                        <View style={styles.checklistSection}>
                            <Text style={styles.checklistTitle}>Classes & Requirements</Text>
                            {classes.map(cls => {
                                const isCompleted = completedClassIDs.has(cls.id);
                                const classReqs = cls.Requirements || [];
                                return (
                                    <View key={cls.id} style={styles.classWithRequirements}>
                                        <Pressable
                                            onPress={() => {
                                                if (!loggedInUser?.can?.("Manage Training Enrollments")) return;
                                                if (isGraduated && isCompleted) return;
                                                isCompleted ? onUnmarkClass(cls.id) : onMarkClass(cls.id);
                                            }}
                                            style={[styles.checklistItem, isGraduated && isCompleted && { opacity: 0.6 }]}
                                        >
                                            {pendingClassIDs?.has(cls.id)
                                                ? <ActivityIndicator size={20} />
                                                : <Unicon name={isCompleted ? "checkmark-circle" : "ellipse-outline"} size={20} colorVariant={isCompleted ? "success" : "textSecondary"} />}
                                            <Text style={styles.checklistItemText(isCompleted)}>{cls.name}</Text>
                                        </Pressable>
                                        {classReqs.length > 0 && (
                                            <View style={styles.nestedRequirements}>
                                                {classReqs.map(req => {
                                                    const isReqCompleted = completedReqIDs.has(req.id);
                                                    return (
                                                        <Pressable
                                                            key={req.id}
                                                            onPress={() => {
                                                                if (!loggedInUser?.can?.("Manage Training Enrollments")) return;
                                                                if (isGraduated && isReqCompleted) return;
                                                                isReqCompleted ? onUnmarkReq(req.id) : onMarkReq(req.id);
                                                            }}
                                                            style={[styles.nestedReqItem, isGraduated && isReqCompleted && { opacity: 0.6 }]}
                                                        >
                                                            {pendingReqIDs?.has(req.id)
                                                                ? <ActivityIndicator size={16} />
                                                                : <Unicon name={isReqCompleted ? "checkmark-circle" : "ellipse-outline"} size={16} colorVariant={isReqCompleted ? "success" : "textSecondary"} />}
                                                            <Text style={styles.nestedReqText(isReqCompleted)}>{req.name}</Text>
                                                        </Pressable>
                                                    );
                                                })}
                                            </View>
                                        )}
                                    </View>
                                );
                            })}
                        </View>
                    )}
                </View>
            )}
        </View>
    );
});

// ============ STYLES ============
const styles = StyleSheet.create(({ colors, fonts, spacing, typography }) => ({
    container: { flex: 1, backgroundColor: 'transparent' },
    twoColumn: { flex: 1, flexDirection: 'row' },
    leftPanel: { flex: 1, maxWidth: 420 },
    leftPanelContent: { padding: 16, paddingBottom: 100 },
    rightPanel: { flex: 2 },
    tabContent: { flex: 1, padding: spacing.md, maxWidth: 800, width: '100%', alignSelf: 'center' },

    // Header Card
    headerCard: { backgroundColor: colors.surface, borderRadius: 16, padding: 20, marginBottom: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 8, elevation: 3 },
    infoHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, marginBottom: spacing.md },
    moduleIcon: { width: 52, height: 52, borderRadius: 14, backgroundColor: colors.trainingContainer, alignItems: 'center', justifyContent: 'center' },
    infoHeaderText: { flex: 1, gap: spacing.xs },
    moduleName: { fontSize: 18, fontFamily: fonts.heading, color: colors.text },
    statusBadge: (bg, text) => ({ paddingVertical: 2, paddingHorizontal: 8, borderRadius: 6, borderWidth: 1, borderColor: colors[text], backgroundColor: colors[bg], alignSelf: 'flex-start' }),
    statusBadgeText: (text) => ({ fontSize: 11, fontFamily: fonts.bold, color: colors[text] }),
    moduleDescription: { fontSize: typography.body, color: colors.textSecondary, marginBottom: spacing.md, lineHeight: 20 },
    teachersSection: { marginBottom: spacing.md },
    teacherLabel: { fontSize: 11, color: colors.textSecondary, marginBottom: spacing.xs },
    teachersList: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
    teacherChip: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, backgroundColor: colors.background, borderRadius: 20, paddingVertical: 4, paddingLeft: 4, paddingRight: spacing.sm, borderWidth: 1, borderColor: colors.border, _web: { transition: 'all 0.15s ease', cursor: 'pointer', _hover: { backgroundColor: colors.hover } } },
    teacherName: { fontSize: 13, fontFamily: fonts.medium, color: colors.text },

    // Stats Row
    statsRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 14, borderTopWidth: 1, borderBottomWidth: 1, borderColor: colors.border, marginBottom: 14 },
    statItem: { flex: 1, alignItems: 'center' },
    statDivider: { width: 1, height: 28, backgroundColor: colors.border },
    statLabel: { fontSize: 10, color: colors.textSecondary, textTransform: 'uppercase', marginBottom: 2 },
    statValue: { fontSize: 13, fontFamily: fonts.bold, color: colors.text },

    // Action Buttons
    actionRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },

    // Info Section (classes card on left panel)
    infoSection: { backgroundColor: colors.surface, borderRadius: 16, padding: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 4, elevation: 2 },
    sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.sm },
    sectionTitle: { fontSize: 12, fontFamily: fonts.bold, color: colors.textSecondary, textTransform: 'uppercase' },
    sectionAddButton: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 4, paddingHorizontal: spacing.sm, borderRadius: 6, backgroundColor: colors.primaryContainer, _web: { transition: 'all 0.15s ease', cursor: 'pointer', _hover: { opacity: 0.8 } } },
    sectionAddButtonText: { fontSize: 12, fontFamily: fonts.medium, color: colors.primary },
    emptyText: { fontSize: typography.caption, color: colors.textSecondary, fontStyle: 'italic' },

    // Class cards
    classCard: { backgroundColor: colors.surface, borderRadius: 10, padding: spacing.md, marginBottom: spacing.sm, _web: { transition: 'all 0.15s ease' } },
    classCardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
    classCardLeft: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm, flex: 1 },
    classNumber: { width: 28, height: 28, borderRadius: 8, backgroundColor: colors.primaryContainer, alignItems: 'center', justifyContent: 'center' },
    classNumberText: { fontSize: 13, fontFamily: fonts.bold, color: colors.primary },
    className: { fontSize: typography.body, fontFamily: fonts.medium, color: colors.text },
    classDescription: { fontSize: typography.caption, color: colors.textSecondary, marginTop: 2 },
    classReqList: { flex:1, marginTop: spacing.sm, gap: 8 },
    reqChip: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 2},
    reqChipText: { fontSize: 14, color: colors.text },
    reqChipType: {  fontSize: 12, color: colors.textSecondary, paddingTop:5, fontStyle:'italic' },
 
    // Prerequisite
    prerequisiteRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: spacing.md, paddingVertical: spacing.xs },
    prerequisiteLabel: { fontSize: 12, color: colors.textSecondary },
    prerequisiteLink: { fontSize: 12, fontFamily: fonts.medium, color: colors.training, textDecorationLine: 'underline' },

    // Requirement cards
    reqCard: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: colors.surface, borderRadius: 10, padding: spacing.md, marginBottom: spacing.sm, _web: { transition: 'all 0.15s ease' } },
    reqCardLeft: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flex: 1 },
    reqName: { fontSize: typography.body, fontFamily: fonts.medium, color: colors.text },
    reqMetaRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginTop: 2 },
    reqType: { fontSize: 11, color: colors.textSecondary },
    reqDueDate: (overdue) => ({ fontSize: 11, color: overdue ? colors.error : colors.textSecondary, fontFamily: overdue ? fonts.bold : fonts.regular }),

    // Class-level file attachments
    classFilesSection: { marginTop: spacing.sm, paddingTop: spacing.sm, borderTopWidth: 1, borderTopColor: colors.border },
    classFilesHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
    classFilesLabel: { flexDirection: 'row', alignItems: 'center', gap: 4 },
    classFilesLabelText: { fontSize: 11, color: colors.textSecondary },
    classFileUploadButton: { padding: 4, borderRadius: 6, _web: { cursor: 'pointer', transition: 'all 0.15s ease', _hover: { opacity: 0.7 } } },
    classFileItem: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 6, _web: { cursor: 'pointer', transition: 'all 0.15s ease', _hover: { opacity: 0.8 } } },
    classFileItemBorder: { borderBottomWidth: 1, borderBottomColor: colors.border },
    classFileName: { fontSize: 12, fontFamily: fonts.medium, color: colors.primary, flex: 1 },

    // Bulk enrollment preview
    previewSection: { marginTop: spacing.md },
    previewTitle: { fontSize: 13, fontFamily: fonts.medium, color: colors.text, marginBottom: spacing.sm },
    previewList: { maxHeight: 250 },
    previewPerson: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: 6 },
    previewPersonName: { fontSize: typography.body, color: colors.text },

    // Enrollment cards
    enrollmentCard: { backgroundColor: colors.surface, borderRadius: 10, marginBottom: spacing.md, overflow: 'hidden', _web: { transition: 'all 0.15s ease' } },
    enrollmentHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: spacing.md },
    enrollmentPerson: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flex: 1 },
    enrollmentName: { fontSize: typography.body, fontFamily: fonts.medium, color: colors.text },
    enrollmentMeta: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginTop: 2 },
    enrollmentPercent: { fontSize: 12, fontFamily: fonts.bold, color: colors.primary },
    enrollmentActions: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
    miniProgressBarBg: { height: 3, backgroundColor: colors.surfaceVariant },
    miniProgressBarFill: (percent) => ({ height: 3, width: `${percent}%`, backgroundColor: percent === 100 ? colors.success : colors.primary }),
    enrollmentDetails: { padding: spacing.md, backgroundColor: colors.background },

    // Checklist
    checklistSection: { marginBottom: spacing.md },
    checklistTitle: { fontSize: 12, fontFamily: fonts.bold, color: colors.textSecondary, marginBottom: spacing.xs },
    checklistItem: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: 6 },
    checklistItemText: (completed) => ({ fontSize: typography.body, color: completed ? colors.success : colors.text, textDecorationLine: completed ? 'line-through' : 'none' }),
    checklistItemType: { fontSize: 10, color: colors.textTertiary, backgroundColor: colors.surfaceVariant, paddingHorizontal: 4, paddingVertical: 1, borderRadius: 4, marginLeft: 'auto' },

    // Class with nested requirements
    classWithRequirements: { marginBottom: spacing.sm },
    nestedRequirements: { marginLeft: 28, marginTop: 4, gap: 4 },
    nestedReqItem: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: 4 },
    nestedReqText: (completed) => ({ fontSize: 13, color: completed ? colors.success : colors.textSecondary, textDecorationLine: completed ? 'line-through' : 'none' }),

    // Tab toolbar (search + icon buttons)
    tabToolbar: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, padding: spacing.md, paddingBottom: 0, maxWidth: 800, width: '100%', alignSelf: 'center' },
    tabToolbarSearch: { flex: 1 },
    tabToolbarIconBtn: { width: 42, height: 42, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.primaryContainer, _web: { cursor: 'pointer', transition: 'all 0.15s ease', _hover: { opacity: 0.8 } } },

    // Schedule tab container
    scheduleTabContainer: { flex: 1 },
    scheduleActionRow: { flexDirection: 'row', gap: spacing.sm, padding: spacing.md, paddingBottom: 0, maxWidth: 800, width: '100%', alignSelf: 'center' },

    // Instance cards
    instanceCard: { backgroundColor: colors.surface, borderRadius: 10, padding: spacing.md, marginBottom: spacing.sm, _web: { transition: 'all 0.15s ease', cursor: 'pointer', _hover: { backgroundColor: colors.hover } } },
    instanceHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: spacing.sm },
    instanceNumber: { width: 28, height: 28, borderRadius: 8, backgroundColor: colors.primaryContainer, alignItems: 'center', justifyContent: 'center' },
    instanceNumberText: { fontSize: 13, fontFamily: fonts.bold, color: colors.primary },
    instanceClassName: { fontSize: typography.body, fontFamily: fonts.medium, color: colors.text },
    instanceDate: { fontSize: typography.caption, color: colors.textSecondary, marginTop: 2 },
    instanceNotes: { fontSize: 12, color: colors.textSecondary, marginTop: 4, fontStyle: 'italic' },
    instanceTitleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' },
    termBadge: { paddingVertical: 2, paddingHorizontal: 8, borderRadius: 6, backgroundColor: colors.secondaryContainer, alignSelf: 'flex-start' },
    termBadgeText: { fontSize: 11, fontFamily: fonts.bold, color: colors.secondary },
    teacherRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.sm, alignSelf: 'flex-start' },
    teacherNameText: { fontSize: 13, fontFamily: fonts.body, color: colors.textSecondary },
    assignmentBadge: (bg) => ({ paddingVertical: 1, paddingHorizontal: 6, borderRadius: 4, backgroundColor: colors[bg] }),
    assignmentBadgeText: (text) => ({ fontSize: 10, fontFamily: fonts.bold, color: colors[text] }),
    termFilterRow: { padding: spacing.md, paddingBottom: 0, maxWidth: 800, width: '100%', alignSelf: 'center' },
    termManageRow: { flexDirection: 'row', gap: spacing.sm, paddingHorizontal: spacing.md, paddingBottom: spacing.sm, maxWidth: 800, width: '100%', alignSelf: 'center' },
    termManageBtn: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, paddingVertical: spacing.xs, paddingHorizontal: spacing.sm, borderRadius: 8, backgroundColor: colors.surfaceVariant },
    termManageBtnText: { fontSize: 13, fontFamily: fonts.medium, color: colors.primary },
    termManageBtnDanger: { backgroundColor: colors.errorContainer },
    termManageBtnTextDanger: { color: colors.error },
    // Enrollment status badges (used in EnrollmentRow)
    progressBadge: (bg) => ({ paddingVertical: 1, paddingHorizontal: 6, borderRadius: 4, backgroundColor: colors[bg], alignSelf: 'flex-start', marginTop: 2 }),
    progressBadgeText: (text) => ({ fontSize: 10, fontFamily: fonts.bold, color: colors[text] }),
}));
